import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

const fetchOderItems = (orderId) => {
    const params = {
        model: `Table Order`,
        name: orderId,
        method: `get_items`,
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

export const useTableOrderItems = (orderId, onSuccess, onError) => {
    return useQuery(
        ["getTableOrderItems", orderId],
        async () => {
            const data = await fetchOderItems(orderId);
            return data.data.message;
        },
        {
            onSuccess,
            onError,
            enabled: !!orderId,
            cacheTime: 0,
            refetchOnMount: true,
        }
    );
};

export const useTableOrdersItems = (orders, onSuccess, onError) => {
    return useQueries(
        orders.map((order) => {
            return {
                queryKey: ["getTableOrderItemsParallel", order],
                queryFn: () => fetchOderItems(order),
            };
        }),
        {
            enabled: !!orders && orders.length > 0,
            cacheTime: 0,
        }
    );
};

const fetchTableOrders = (tableId) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `orders_list`,
        args: "{}",
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

const pushItemToOrder = async (orderId, item) => {
    try {
        const itemToSend = JSON.stringify(item);
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `_push_item`,
            args: itemToSend,
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

export const usePushItemToOrderData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, item }) => {
            item.addons = JSON.stringify(item.addons);
            const data = await pushItemToOrder(orderId, item);
            return data;
        },
        {
            onSettled: (data, error, { orderId }) => {
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
                queryClient.refetchQueries(["getTableOrderItems", orderId]);
            },
        }
    );
};

const updateOrderItem = async (orderId, item, qty) => {
    try {
        const details = JSON.stringify({ identifier: item, qty: qty });

        const params = {
            model: `Table Order`,
            name: orderId,
            method: "update_entry_quantity",
            args: details,
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

export const useUpdateItemInOrderData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, item, qty }) => {
            const data = await updateOrderItem(orderId, item, qty);
            return data;
        },
        {
            onSettled: (data, error, { orderId }) => {
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
                queryClient.refetchQueries(["getTableOrderItems", orderId]);
            },
        }
    );
};

const removeItemFromOrder = async (orderId, item) => {
    try {
        let itemsToDelete = { item: item.identifier };
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `delete_item`,
            args: JSON.stringify(itemsToDelete),
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

export const useRemoveItemFromTableOrderData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, item }) => {
            const data = await removeItemFromOrder(orderId, item);
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                // Refresh the query for the table order items.
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
                queryClient.refetchQueries(["getTableOrderItems", orderId]);
            },
        }
    );
};

export const useRemoveItemsFromTableOrderData = () => {
    return useMutation(async ({ items }) => {
        // Use useQueries to handle multiple parallel queries for item removals
        const removalQueries = items.map((item) => ({
            queryKey: ["removeItemFromOrder", item.order_name, item.entry_name], // Custom query key
            queryFn: () => removeItemFromOrder(item.order_name, item), // Custom query function
        }));

        await Promise.all(removalQueries.map((query) => query.queryFn()));

        // Optionally, you can return the result of each removal query if needed.
        return removalQueries.map((query) => query.data);
    });
};

const divideOrder = async (orderId, divisionPayload) => {
    // {"items":{"entry_9ef4-9c-4f3-bc8-8a4fe":{"name":"09f9cea9e5","qty":1,"identifier":"entry_obj_d83e-a1-434-ba9-76220"},"entry_6bf2a97e-4f74-4ba7-a1e3-2bebad025b13":{"name":"3c4d59b9ff","qty":1,"identifier":"entry_obj_75c6-c0-43f-b0f-eb158"}},"client":"obj_3a5d-bf-457-825-0c237"}
    try {
        const escapedPayload = JSON.stringify(divisionPayload);
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `divide`,
            args: escapedPayload,
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

const onHouseOrder = async (orderId) => {
    try {
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `on_the_house`,
            args: JSON.stringify({}),
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

const defferOrder = async (orderId, customer, discount_percentage) => {
    try {
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `defer_payment`,
            args: JSON.stringify({ customer: customer, discount_percentage }),
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

export const useDivideOrderItemsData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, divisionPayload }) => {
            const data = await divideOrder(orderId, divisionPayload);
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                queryClient.refetchQueries(["getTableOrderItems"]);
            },
        }
    );
};

export const useOrderItemOnHouse = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, divisionPayload }) => {
            const data = await onHouseOrder(orderId);
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                queryClient.refetchQueries(["getTableOrderItems"]);
            },
        }
    );
};

export const useDefferOrderItem = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, customer, discount_percentage }) => {
            const data = await defferOrder(
                orderId,
                customer,
                discount_percentage
            );
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                queryClient.refetchQueries(["getTableOrderItems"]);
            },
        }
    );
};
